/**
 * Component that shows auto complete options to the user, similar to a code editor's
 * auto-completion system.
 */

import { API } from '../../api';
import { AutoComplete, Button, Input, Popover } from 'antd';
import {
  HistoryOutlined,
  LeftOutlined,
  RedoOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { cloneDeep } from 'lodash';
import { formatDate } from '../../utils/util';
import React from 'react';

const CATEGORY_IDX_MAP = {
  Booleans: 0,
  Functions: 1,
  Columns: 2,
};
class ValueCell extends React.Component {
  constructor(props) {
    super(props);
    let initialCellValue = '';

    // Case: plain string is passed in
    if (typeof props.value === 'string') {
      initialCellValue = props.value;
    } else {
      initialCellValue = props.value.props.children;
    }

    // eslint-disable-next-line react/state-in-constructor
    this.state = {
      /**
       * Initial value of the cell, which will be updated whenever
       * (1) an option is selected or (2) the user types in input.
       */
      cellValue: initialCellValue,
      /**
       * Where in the input the selected option will be inserted.
       */
      showCellHistory: false,
      cellHistory: [],
      cellHistoryIndex: 0,
      selectionPosition: initialCellValue.length,
      suggestions: this.props.suggestions,
    };
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }

  /**
   * Called on any change to the AutoComplete input.
   */
  handleChange = newVal => {
    const val = newVal;
    this.setState({
      cellValue: val,
    });
    if (!this.props.autoCompleteMode) this.searchSuggestions(val);
  };

  /**
   * Called when the user presses down on mouse in input.
   * Gets the cursor position within the input.
   */
  handleMouseUp = e => {
    if (e.target.tagName.toLowerCase() === 'input') {
      const pos = e.target.selectionStart;
      this.setState({
        selectionPosition: pos,
      });
    }
  };

  /**
   * When enter key is pressed, save data.
   */
  handleKeyDown = e => {
    if (e.key === 'Enter') this.props.handleValueChange(this.state.cellValue);
  };

  /**
   * Called when an AutoComplete option is selected.
   *
   * Requires a strategy, either 'insert' or 'replace', which determines how
   * the selected value (option) is used in the cell data.
   *
   * Currently, only insert is used as the strategy, but may include replace
   * in the future.
   */
  handleSelect = (selectedValue, strategy) => {
    const cellValue = this.state.cellValue.toString();
    const pos = this.state.selectionPosition;
    let newValue;

    if (strategy === 'insert') {
      newValue =
        cellValue.slice(0, Math.max(0, pos)) + selectedValue + cellValue.slice(Math.max(0, pos));
    }

    if (strategy === 'replace') {
      newValue = selectedValue.toString();
    }

    this.setState({
      cellValue: newValue,
      selectionPosition: newValue.length,
    });
  };

  /**
   * Search a single category for the given value,
   * and returns all related terms.
   */
  searchSuggestionCategory = (searchValue, category) => {
    const idx = CATEGORY_IDX_MAP[category];
    const newSuggestionsForCategory = cloneDeep(this.props.suggestions[idx]);
    newSuggestionsForCategory.options = [];

    for (let i = 0; i < this.props.suggestions[idx].options.length; i += 1) {
      const d = this.props.suggestions[idx].options[i];

      if (d.value.toLowerCase().includes(searchValue.toLowerCase())) {
        newSuggestionsForCategory.options.push(d);
      }
    }

    return newSuggestionsForCategory.options;
  };

  /**
   * Update the suggestions given in the AutoComplete based on what the user has typed
   * in the input. Updates suggestion for each category (boolean, functions, and variables).
   */
  searchSuggestions = searchValue => {
    const newKeywordSuggestions = this.searchSuggestionCategory(searchValue, 'Booleans');
    const newFunctionsSuggestions = this.searchSuggestionCategory(searchValue, 'Functions');
    const newVariablesSuggestions = this.searchSuggestionCategory(searchValue, 'Columns');
    const newSuggestions = [
      {
        label: Object.keys(CATEGORY_IDX_MAP)[0],
        options: newKeywordSuggestions,
      },
      {
        label: Object.keys(CATEGORY_IDX_MAP)[1],
        options: newFunctionsSuggestions,
      },
      {
        label: Object.keys(CATEGORY_IDX_MAP)[2],
        options: newVariablesSuggestions,
      },
    ];
    this.setState({
      suggestions: newSuggestions,
    });
  };

  /**
   * Gets cell revision history for one mapping cell
   */
  getCellRevisionHistory = async () => {
    // do not allow additional requests while history still open
    if (this.state.showCellHistory) return;

    try {
      const { data } = await API.get(`/mapping/history/${this.props.record.id}`);
      const { history } = data;
      this.setState({
        showCellHistory: true,
        cellHistory: history,
      });
    } catch (e) {
      // logging?
    }
  };

  handleCellHistoryClose = () => {
    this.setState({ showCellHistory: false });
  };

  /**
   * Called when user clicks the "Restore" button in cell history.
   * Sets the value of the cell to the previous (restored) value.
   */
  handleCellHistoryRestore = () => {
    const { cellHistory, cellHistoryIndex } = this.state;
    this.setState({ cellValue: cellHistory[cellHistoryIndex].new_value });
  };

  renderCellHistoryContent = () => {
    const { cellHistory, cellHistoryIndex } = this.state;
    const history = cellHistory[cellHistoryIndex];
    const close = (
      <Button type="link" data-cy="cell-history-close" onClick={this.handleCellHistoryClose}>
        Close
      </Button>
    );
    // only show restore button for previous values in history, not current one
    const restore =
      cellHistoryIndex > 0 ? (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a data-cy="cell-history-restore" onClick={this.handleCellHistoryRestore}>
          <RedoOutlined /> Restore
        </a>
      ) : null;

    if (!history) {
      return (
        <>
          <p>No Revision History</p>
          {close}
        </>
      );
    }

    const { timestamp } = history;
    const newValue = history.new_value;
    const userName = history.user_name;
    const revisionDate = formatDate(timestamp);
    return (
      <>
        <div style={{ display: 'flex' }}>
          <UserOutlined style={{ fontSize: '40px', marginRight: '10px' }} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <strong style={{ margin: '0px', padding: '0px' }}> {userName || 'Unknown'}</strong>
            <span style={{ margin: '0px', padding: '0px', color: 'grey' }}>{revisionDate}</span>
          </div>
        </div>
        <br />
        <p>
          <strong>Added:</strong> {newValue}
        </p>
        {restore}
        {close}
      </>
    );
  };

  renderCellHistoryHeader = () => {
    const { cellHistory, cellHistoryIndex } = this.state;
    const allowLeftArrow = cellHistory[cellHistoryIndex + 1];
    const allowRightArrow = cellHistory[cellHistoryIndex - 1];
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit History
        <div>
          <LeftOutlined
            id="cell-history-left"
            style={{ marginRight: '30px', color: allowLeftArrow ? '#1890ff' : 'grey' }}
            onClick={
              allowLeftArrow
                ? () => this.setState({ cellHistoryIndex: cellHistoryIndex + 1 })
                : null
            }
          />
          <RightOutlined
            id="cell-history-right"
            style={{ color: allowRightArrow ? '#1890ff' : 'grey' }}
            onClick={
              allowRightArrow
                ? () => this.setState({ cellHistoryIndex: cellHistoryIndex - 1 })
                : null
            }
          />
        </div>
      </div>
    );
  };

  /**
   * On blur, check to see if the cell history button was clicked. If so,
   * don't do anything since we just want the version history to appear in
   * this case.
   */
  handleAutocompleteBlur = e => {
    if (e.relatedTarget == null || !e.relatedTarget.id.includes('cell-history-')) {
      this.props.handleValueChange(this.state.cellValue);
    }
  };

  /**
   * Render input in the default case, when there is already input content.
   */

  renderDefaultInput = () => {
    const { forwardedRef, record, dataIndex, width } = this.props;
    const { cellValue } = this.state;
    return (
      <AutoComplete
        style={{ width }}
        onKeyDown={this.handleKeyDown}
        value={cellValue}
        onChange={this.handleChange}
        ref={forwardedRef}
        data-cy={`cell-${record.id}-${dataIndex}-value-input`}
      >
        <Input onBlur={this.handleAutocompleteBlur} suffix={this.renderPopover()} />
      </AutoComplete>
    );
  };

  /**
   * Render input in the case when function suggestions should be shown for auto completion.
   */
  renderAutoCompleteInput = () => {
    const { forwardedRef, record, dataIndex, width } = this.props;
    const { cellValue, suggestions, showCellHistory } = this.state;
    return (
      <AutoComplete
        options={showCellHistory ? [] : suggestions}
        style={{ width }}
        onSelect={v => this.handleSelect(v, 'replace')}
        defaultValue={cellValue}
        value={cellValue}
        onKeyDown={this.handleKeyDown}
        onMouseUp={e => this.handleMouseUp(e)}
        onChange={this.handleChange}
        ref={forwardedRef}
        data-cy={`cell-${record.id}-${dataIndex}-value-input`}
        defaultOpen
      >
        <Input onBlur={this.handleAutocompleteBlur} suffix={this.renderPopover()} />
      </AutoComplete>
    );
  };

  renderPopover = () => {
    return (
      <Popover
        overlayStyle={{ width: '300px', overflowWrap: 'break-word' }}
        placement="rightTop"
        content={this.renderCellHistoryContent()}
        title={this.renderCellHistoryHeader()}
        trigger="click"
        open={this.state.showCellHistory}
        onOpenChange={this.handleVisibleChange}
        data-cy="cell-history-popover"
      >
        <HistoryOutlined
          id="cell-history-toggle"
          data-cy="cell-history-toggle"
          onClick={this.getCellRevisionHistory}
        />
      </Popover>
    );
  };

  render() {
    const { autoCompleteMode } = this.props;

    if (autoCompleteMode) {
      return this.renderAutoCompleteInput();
    }

    return this.renderDefaultInput();
  }
}

export default ValueCell;
